import React, { useEffect, useState } from "react";
import { Loader2, Trash2, Users, Globe2, Check, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { adminApi } from "@/lib/adminApi";
import AdminPageHeader from "@/components/admin/AdminPageHeader";

function fmtDate(iso) {
    try {
        return new Intl.DateTimeFormat("de-AT", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
    } catch {
        return iso;
    }
}

const statusStyles = {
    pending: { label: "Pending", color: "bg-caireen-yellow/20 text-caireen-ink", icon: Clock },
    approved: { label: "Approved", color: "bg-caireen-olive/15 text-caireen-olive", icon: Check },
    rejected: { label: "Rejected", color: "bg-caireen-red/10 text-caireen-red", icon: X },
};

const typeLabels = {
    ordinary: "Ordinary",
    extraordinary: "Extraordinary",
    supporting: "Supporting",
};

export default function AdminMemberships() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [selected, setSelected] = useState(null);
    const [notes, setNotes] = useState("");
    const [savingStatus, setSavingStatus] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = () => {
        setLoading(true);
        adminApi
            .memberships({ limit: 200 })
            .then(setData)
            .catch(() => toast.error("Failed to load"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const onDelete = async (id) => {
        setDeleting(id);
        try {
            await adminApi.deleteMembership(id);
            toast.success("Deleted");
            setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== id), total: d.total - 1 }));
            if (selected?.id === id) setSelected(null);
        } catch {
            toast.error("Failed to delete");
        } finally {
            setDeleting(null);
        }
    };

    const updateStatus = async (status) => {
        if (!selected) return;
        setSavingStatus(true);
        try {
            await adminApi.updateMembership(selected.id, { status, notes });
            toast.success(`Marked as ${status}`);
            setData((d) => ({
                ...d,
                items: d.items.map((x) => (x.id === selected.id ? { ...x, status, notes } : x)),
            }));
            setSelected((s) => ({ ...s, status, notes }));
        } catch {
            toast.error("Failed to update");
        } finally {
            setSavingStatus(false);
        }
    };

    const onExport = async () => {
        setExporting(true);
        try {
            await adminApi.exportCsv("memberships");
        } catch {
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    const openRow = (m) => {
        setSelected(m);
        setNotes(m.notes || "");
    };

    return (
        <div data-testid="admin-memberships">
            <AdminPageHeader
                eyebrow="Admin · Community"
                title="Membership applications"
                count={data.total}
                onRefresh={load}
                onExport={onExport}
                exporting={exporting}
            />

            <div className="bg-white rounded-3xl ring-1 ring-caireen-ink/10 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-24 text-caireen-ink/40">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : data.items.length === 0 ? (
                    <div className="text-center py-20 font-sans text-sm text-caireen-ink/55">
                        <Users className="w-8 h-8 mx-auto mb-3 text-caireen-ink/30" />
                        No applications yet.
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-caireen-cream/60">
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Applied</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Name</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Email</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Type</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Country</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Status</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.items.map((m) => {
                                const st = statusStyles[m.status || "pending"];
                                return (
                                    <TableRow key={m.id} className="hover:bg-caireen-cream/40 cursor-pointer" onClick={() => openRow(m)}>
                                        <TableCell className="font-sans text-sm text-caireen-ink/70 whitespace-nowrap">{fmtDate(m.created_at)}</TableCell>
                                        <TableCell className="font-sans text-sm font-semibold">{m.full_name}</TableCell>
                                        <TableCell className="font-sans text-sm text-caireen-ink/70">{m.email}</TableCell>
                                        <TableCell className="font-sans text-sm text-caireen-ink/70">{typeLabels[m.member_type] || m.member_type}</TableCell>
                                        <TableCell className="font-sans text-sm text-caireen-ink/70 inline-flex items-center gap-1.5">
                                            <Globe2 className="w-3.5 h-3.5 text-caireen-ink/40" />
                                            {m.country || "—"}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full font-sans text-[10px] uppercase tracking-widest ${st.color}`}>
                                                <st.icon className="w-3 h-3" />
                                                {st.label}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                data-testid={`membership-delete-${m.id}`}
                                                variant="ghost"
                                                size="sm"
                                                disabled={deleting === m.id}
                                                onClick={() => onDelete(m.id)}
                                                className="text-caireen-red hover:bg-caireen-red/10 rounded-full"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">{selected?.full_name}</DialogTitle>
                        <DialogDescription className="font-sans">
                            {selected?.email} · {selected && typeLabels[selected.member_type]} · {selected?.country || "—"} ·{" "}
                            {selected && fmtDate(selected.created_at)}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div>
                            <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55 mb-1">Motivation</p>
                            <div className="bg-caireen-cream/60 rounded-2xl p-4 font-sans text-sm whitespace-pre-wrap leading-relaxed text-caireen-ink/85">
                                {selected?.motivation || "—"}
                            </div>
                        </div>

                        <div>
                            <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55 mb-1">Internal notes</p>
                            <Textarea
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15"
                                data-testid="membership-notes"
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2 flex-wrap">
                        <Button
                            data-testid="membership-reject"
                            variant="outline"
                            disabled={savingStatus}
                            onClick={() => updateStatus("rejected")}
                            className="rounded-full border-caireen-red/30 text-caireen-red hover:bg-caireen-red/10"
                        >
                            <X className="w-4 h-4 mr-1.5" />
                            Reject
                        </Button>
                        <Button
                            data-testid="membership-pending"
                            variant="outline"
                            disabled={savingStatus}
                            onClick={() => updateStatus("pending")}
                            className="rounded-full border-caireen-ink/15"
                        >
                            <Clock className="w-4 h-4 mr-1.5" />
                            Pending
                        </Button>
                        <Button
                            data-testid="membership-approve"
                            disabled={savingStatus}
                            onClick={() => updateStatus("approved")}
                            className="rounded-full bg-caireen-olive hover:bg-caireen-olive/90 text-white"
                        >
                            <Check className="w-4 h-4 mr-1.5" />
                            Approve
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
